// src/components/DetailedAnalysis.tsx
import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area, ScatterChart, Scatter
} from 'recharts';
import { 
  TrendingUp, Users, AlertTriangle, Target, DollarSign, 
  Calendar, Brain, Zap, Clock, CheckCircle, X, Download, 
  Share2, Maximize2, Activity, PieChart as PieChartIcon,
  BarChart3, LineChart as LineChartIcon, GitBranch, Cpu,
  DollarSign as DollarIcon, TrendingDown, Award, Target as TargetIcon,
  MessageSquare, Play, Pause, RefreshCw, ChevronRight, ExternalLink,
  Mail, FileText, Database, Settings, Code, UserPlus, Bell, Layers
} from 'lucide-react';

interface DetailedAnalysisProps {
  analysisData: any;
  capability: string;
  onClose: () => void;
  autoStartWorkflow?: boolean;
}

const connectorIcons: Record<string, React.ReactNode> = {
  'outlook': <Mail size={16} className="text-blue-600" />,
  'calendar': <Calendar size={16} className="text-red-600" />,
  'message': <MessageSquare size={16} className="text-green-600" />,
  'database': <Database size={16} className="text-purple-600" />,
  'monitoring': <BarChart3 size={16} className="text-blue-500" />,
  'review': <CheckCircle size={16} className="text-green-600" />,
  'alert': <AlertTriangle size={16} className="text-red-600" />,
  'reallocation': <RefreshCw size={16} className="text-blue-600" />,
  'process': <Settings size={16} className="text-gray-600" />,
  'technical': <Code size={16} className="text-indigo-600" />,
  'requirements': <FileText size={16} className="text-blue-600" />,
  'team': <UserPlus size={16} className="text-green-600" />,
  'design': <Layers size={16} className="text-purple-600" />,
  'development': <Code size={16} className="text-gray-800" />,
  'testing': <CheckCircle size={16} className="text-green-600" />,
  'analysis': <PieChartIcon size={16} className="text-blue-600" />,
  'approval': <CheckCircle size={16} className="text-green-600" />,
  'planning': <Target size={16} className="text-red-600" />,
  'communication': <Bell size={16} className="text-yellow-600" />,
  'briefing': <FileText size={16} className="text-blue-600" />,
  'content': <Settings size={16} className="text-green-600" />,
  'channels': <Share2 size={16} className="text-purple-600" />,
  'launch': <Zap size={16} className="text-yellow-600" />,
  'token': <Code size={16} className="text-indigo-600" />,
  'productivity': <TrendingUp size={16} className="text-green-600" />,
  'quality': <CheckCircle size={16} className="text-blue-600" />,
  'roi': <DollarSign size={16} className="text-green-600" />,
};

const getConnectorForStep = (capability: string, stepIndex: number) => {
  switch (capability) {
    case 'team-rebalancing':
      const teamRebalancingConnectors = ['outlook', 'calendar', 'database', 'message', 'monitoring', 'review'];
      return teamRebalancingConnectors[stepIndex] || null;
    
    case 'bottleneck-diagnosis':
      const bottleneckConnectors = ['alert', 'reallocation', 'process', 'technical', 'monitoring', 'planning'];
      return bottleneckConnectors[stepIndex] || null;
    
    case 'initiative-planner':
      const initiativeConnectors = ['requirements', 'team', 'design', 'development', 'testing', 'launch'];
      return initiativeConnectors[stepIndex] || null;
    
    case 'budget-simulator':
      const budgetConnectors = ['analysis', 'review', 'approval', 'planning', 'communication', 'monitoring'];
      return budgetConnectors[stepIndex] || null;
    
    case 'ai-coding-impact':
      const aiCodingConnectors = ['token', 'productivity', 'quality', 'analysis', 'roi', 'monitoring'];
      return aiCodingConnectors[stepIndex] || null;
    
    default:
      return null;
  }
};

function groupPersonnelRecommendations(recommendations: any[]) {
  const groups: Record<string, { category: string; count: number; impact: number; avgImpact: number }> = {};
  
  for (const rec of recommendations) {
    let category = '';
    if (rec.justification.includes('Contractor')) {
      category = 'Contractor Optimization';
    } else if (rec.justification.includes('Low performer')) {
      category = 'Low Performer Management';
    } else if (rec.justification.includes('misaligned')) {
      category = 'Career Development';
    } else if (rec.justification.includes('High potential')) {
      category = 'High Potential Investment';
    } else if (rec.type === 'invest') {
      category = 'Strategic Investment';
    } else if (rec.type === 'retain') {
      category = 'Key Talent Retention';
    } else {
      category = 'Other Adjustments';
    }
    
    if (!groups[category]) {
      groups[category] = { category, count: 0, impact: 0, avgImpact: 0 };
    }
    
    groups[category].count++;
    groups[category].impact += Math.abs(rec.impact || 0);
  }
  
  // Calculate average impact
  Object.values(groups).forEach(group => {
    group.avgImpact = group.count > 0 ? group.impact / group.count : 0;
  });
  
  return Object.values(groups).sort((a, b) => b.impact - a.impact);
}

const DetailedAnalysis: React.FC<DetailedAnalysisProps> = ({ 
  analysisData, capability, onClose, autoStartWorkflow = false 
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'charts' | 'workflow' | 'insights'>('overview');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [simulationProgress, setSimulationProgress] = useState(0);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);

  const generateCapabilitySpecificData = () => {
    if (!analysisData) return {};
    
    switch (capability) {
      case 'team-rebalancing':
        const timelineData = [
          { week: 'W1', productivity: 65, risk: 85, efficiency: 60 },
          { week: 'W2', productivity: 55, risk: 75, efficiency: 65 },
          { week: 'W3', productivity: 70, risk: 55, efficiency: 75 },
          { week: 'W4', productivity: 85, risk: 35, efficiency: 82 },
          { week: 'W5', productivity: 90, risk: 20, efficiency: 88 },
        ];
        
        const skillData = [
          { skill: 'React', level: 85 },
          { skill: 'TypeScript', level: 90 },
          { skill: 'Node.js', level: 80 },
          { skill: 'AWS', level: 70 },
          { skill: 'Leadership', level: 75 },
          { skill: 'Communication', level: 88 }
        ];
        
        const teamImpactData = [
          { name: 'Knowledge Transfer', value: 85 },
          { name: 'Team Morale', value: 65 },
          { name: 'Process Efficiency', value: 75 },
          { name: 'Risk Mitigation', value: 90 },
        ];
        
        const teamWorkflowSteps = [
          { step: 1, title: 'Request Initiation', description: 'Submit HR and manager approvals', duration: '2 days', connector: 'outlook' },
          { step: 2, title: 'Knowledge Transfer Planning', description: 'Schedule sessions and document handover', duration: '3 days', connector: 'calendar' },
          { step: 3, title: 'System Updates', description: 'Update project assignments and permissions', duration: '1 day', connector: 'database' },
          { step: 4, title: 'Team Communication', description: 'Announce changes to both teams', duration: '1 day', connector: 'message' },
          { step: 5, title: 'Transition Phase', description: 'Overlap period and monitoring', duration: '2 weeks', connector: 'monitoring' },
          { step: 6, title: 'Performance Review', description: '30-day post-move assessment', duration: '30 days', connector: 'review' },
        ];
        
        return {
          title: `Generate Capacity Plan: ${analysisData.metadata?.personName || 'Employee'} Move Analysis`,
          summary: `Moving ${analysisData.metadata?.personName || 'the employee'} from ${analysisData.metadata?.fromProjectName || 'source project'} to ${analysisData.metadata?.toProjectName || 'target project'}`,
          timelineData,
          skillData,
          teamImpactData,
          workflowSteps: teamWorkflowSteps,
          metrics: [
            { label: 'Skill Match', value: `${analysisData.successMetrics?.skillMatch || 85}%`, color: 'text-blue-600' },
            { label: 'Success Probability', value: `${analysisData.successMetrics?.probability || 78}%`, color: 'text-green-600' },
            { label: 'Risk Score', value: `${analysisData.riskAssessment?.overallRiskScore || 42}/100`, color: 'text-red-600' },
            { label: 'ROI Period', value: `${analysisData.successMetrics?.estimatedROIMonths || 3} months`, color: 'text-purple-600' },
          ],
          recommendations: [
            'Schedule knowledge transfer sessions within first week',
            'Assign a mentor in the new team for first month',
            'Monitor performance metrics weekly for first 4 weeks',
            'Plan backfill for critical roles if needed',
          ],
          charts: ['timeline', 'skills', 'impact']
        };

      case 'bottleneck-diagnosis':
        const bottleneckData = [
          { category: 'Resource Constraints', value: 65, color: '#ef4444' },
          { category: 'Dependencies', value: 45, color: '#f59e0b' },
          { category: 'Technical Debt', value: 30, color: '#8b5cf6' },
          { category: 'Process Issues', value: 25, color: '#10b981' },
          { category: 'Skill Gaps', value: 20, color: '#3b82f6' },
        ];
        
        const recoveryTimeline = [
          { week: 'Current', progress: 20, risk: 80 },
          { week: 'W1', progress: 35, risk: 65 },
          { week: 'W2', progress: 55, risk: 45 },
          { week: 'W3', progress: 75, risk: 30 },
          { week: 'W4', progress: 90, risk: 15 },
          { week: 'W5', progress: 100, risk: 5 },
        ];
        
        const bottleneckWorkflowSteps = [
          { step: 1, title: 'Immediate Triage', description: 'Identify critical path blockers', duration: '1 day', connector: 'alert' },
          { step: 2, title: 'Resource Reallocation', description: 'Shift resources to high-priority tasks', duration: '2 days', connector: 'reallocation' },
          { step: 3, title: 'Process Adjustments', description: 'Implement daily standups and visibility', duration: '3 days', connector: 'process' },
          { step: 4, title: 'Technical Resolution', description: 'Address technical debt and issues', duration: '1 week', connector: 'technical' },
          { step: 5, title: 'Monitoring Phase', description: 'Track progress and adjust as needed', duration: '2 weeks', connector: 'monitoring' },
          { step: 6, title: 'Prevention Planning', description: 'Implement measures to prevent recurrence', duration: '1 week', connector: 'planning' },
        ];
        
        return {
          title: `Automate Root Cause Analysis: ${analysisData.metadata?.projectName || 'Project'} Analysis`,
          summary: `Root cause analysis for delays in ${analysisData.metadata?.projectName || 'the project'}`,
          bottleneckData,
          recoveryTimeline,
          workflowSteps: bottleneckWorkflowSteps,
          metrics: [
            { label: 'Primary Cause Impact', value: `${analysisData.rootCauseAnalysis?.primaryCause?.impactPercentage || 65}%`, color: 'text-red-600' },
            { label: 'Timeline Delay', value: `${analysisData.impactAssessment?.timelineDelayWeeks || 3} weeks`, color: 'text-orange-600' },
            { label: 'Budget Impact', value: `$${(analysisData.impactAssessment?.budgetImpact || 150000).toLocaleString()}`, color: 'text-purple-600' },
            { label: 'Recovery Confidence', value: `${analysisData.recoveryConfidence || 85}%`, color: 'text-green-600' },
          ],
          recommendations: [
            'Reallocate 2 senior engineers to critical path',
            'Implement daily progress tracking meetings',
            'Address technical debt in 2 key modules',
            'Add temporary contractor for 4 weeks',
          ],
          charts: ['bottleneck', 'recovery']
        };

      case 'initiative-planner':
        const initiativeData = [
          { phase: 'Planning', cost: 50000, duration: 2 },
          { phase: 'Development', cost: 300000, duration: 12 },
          { phase: 'Testing', cost: 80000, duration: 4 },
          { phase: 'Deployment', cost: 50000, duration: 2 },
        ];
        
        const teamComposition = [
          { role: 'Backend Engineers', count: 3, cost: 240000 },
          { role: 'Frontend Engineers', count: 2, cost: 160000 },
          { role: 'DevOps', count: 1, cost: 120000 },
          { role: 'Product Manager', count: 1, cost: 150000 },
          { role: 'QA Engineers', count: 2, cost: 140000 },
        ];
        
        const initiativeWorkflowSteps = [
          { step: 1, title: 'Requirements Gathering', description: 'Define scope and objectives', duration: '2 weeks', connector: 'requirements' },
          { step: 2, title: 'Team Assembly', description: 'Recruit and onboard team members', duration: '3 weeks', connector: 'team' },
          { step: 3, title: 'Architecture Design', description: 'Design system architecture and tech stack', duration: '2 weeks', connector: 'design' },
          { step: 4, title: 'Development Sprints', description: 'Agile development with 2-week sprints', duration: '12 weeks', connector: 'development' },
          { step: 5, title: 'Testing & QA', description: 'Comprehensive testing and bug fixing', duration: '4 weeks', connector: 'testing' },
          { step: 6, title: 'Deployment & Launch', description: 'Production deployment and monitoring', duration: '2 weeks', connector: 'launch' },
        ];
        
        return {
          title: 'Initiative Planning Dashboard',
          summary: 'Comprehensive planning for new strategic initiative',
          initiativeData,
          teamComposition,
          workflowSteps: initiativeWorkflowSteps,
          metrics: [
            { label: 'Team Size', value: analysisData.teamComposition?.recommendedTeam?.length || 9, color: 'text-blue-600' },
            { label: 'Total Budget', value: `$${(analysisData.teamComposition?.totalCost || 810000).toLocaleString()}`, color: 'text-purple-600' },
            { label: 'Timeline', value: `${analysisData.timelineMonths || 6} months`, color: 'text-green-600' },
            { label: 'Success Probability', value: `${analysisData.successMetrics?.probability || 75}%`, color: 'text-orange-600' },
          ],
          recommendations: [
            'Start with 2 senior backend engineers',
            'Use React for frontend, Node.js for backend',
            'Implement CI/CD pipeline from week 2',
            'Budget $50K for training and onboarding',
          ],
          charts: ['budget', 'team', 'timeline']
        };

      case 'budget-simulator':
        // Ensure we have personnel data
        const personnelRecs = analysisData.personnelImpact?.recommendations || [];
        
        // Generate default data if empty
        if (personnelRecs.length === 0) {
          personnelRecs.push(
            {
              type: 'reduce',
              personId: 'contractor-1',
              justification: 'Contractor: Easy to adjust without severance. Saves $45,000 annually',
              impact: 45000,
              timeline: 'Immediate',
              priority: 'high'
            },
            {
              type: 'reduce',
              personId: 'low-perf-1',
              justification: 'Low performer (4/10): Multiple missed deadlines. Saves $85,000 annually',
              impact: 85000,
              timeline: '3 months',
              priority: 'high'
            },
            {
              type: 'optimize',
              personId: 'eng-1',
              justification: 'High performer (9/10) but misaligned: Career development needed',
              impact: -10000,
              timeline: '6 months',
              priority: 'medium'
            },
            {
              type: 'invest',
              personId: 'high-pot-1',
              justification: 'High potential with 9/10 rating: Ready for leadership role development',
              impact: -15000,
              timeline: 'Immediate',
              priority: 'high'
            }
          );
        }
        
        // Ensure headcount change exists
        const headcountChange = analysisData.personnelImpact?.headcountChange || -2;
        const totalPersonnelImpact = analysisData.personnelImpact?.totalPersonnelImpact || 120000;
        
        const contractorCount = personnelRecs.filter((r: any) => 
          r.justification && r.justification.includes('Contractor')
        ).length || 2;
        
        const lowPerformerCount = personnelRecs.filter((r: any) => 
          r.type === 'reduce' && r.justification && r.justification.includes('Low performer')
        ).length || 1;
        
        const highPotentialCount = personnelRecs.filter((r: any) => 
          r.justification && r.justification.includes('High potential')
        ).length || 1;
        
        const departmentImpact = [
          { department: 'Engineering', current: 2000000, new: 1800000, change: -10 },
          { department: 'Marketing', current: 800000, new: 720000, change: -10 },
          { department: 'Sales', current: 1200000, new: 1080000, change: -10 },
          { department: 'Operations', current: 600000, new: 540000, change: -10 },
          { department: 'R&D', current: 1000000, new: 900000, change: -10 },
        ];
        
        const savingsBreakdown = [
          { category: 'Cloud Costs', savings: Math.abs(totalPersonnelImpact) * 0.3, impact: 'low' },
          { category: 'Software Licenses', savings: Math.abs(totalPersonnelImpact) * 0.2, impact: 'medium' },
          { category: 'Contractors', savings: Math.abs(totalPersonnelImpact) * 0.4, impact: 'high' },
          { category: 'Travel & Events', savings: Math.abs(totalPersonnelImpact) * 0.1, impact: 'low' },
        ];

        const implementationTimeline = [
          { month: 'M1', cumulativeSavings: Math.abs(totalPersonnelImpact) * 0.3 },
          { month: 'M2', cumulativeSavings: Math.abs(totalPersonnelImpact) * 0.6 },
          { month: 'M3', cumulativeSavings: Math.abs(totalPersonnelImpact) * 0.8 },
          { month: 'M4', cumulativeSavings: Math.abs(totalPersonnelImpact) * 0.9 },
          { month: 'M5', cumulativeSavings: Math.abs(totalPersonnelImpact) * 0.95 },
          { month: 'M6', cumulativeSavings: Math.abs(totalPersonnelImpact) },
        ];
        
        const budgetWorkflowSteps = [
          { step: 1, title: 'Team Intelligence Analysis', description: 'Analyze individual performance, career alignment, and potential', duration: '1 week', connector: 'analysis' },
          { step: 2, title: 'Contractor Optimization', description: `Review and optimize ${contractorCount} contractor agreements`, duration: '1 week', connector: 'review' },
          { step: 3, title: 'Performance Management', description: lowPerformerCount > 0 ? `Implement improvement plans for ${lowPerformerCount} low performers` : 'Optimize team performance', duration: '2 weeks', connector: 'process' },
          { step: 4, title: 'Career Development Planning', description: 'Schedule sessions for misaligned high performers', duration: '1 week', connector: 'approval' },
          { step: 5, title: 'Budget Implementation', description: 'Apply financial adjustments across systems', duration: '2 days', connector: 'communication' },
          { step: 6, title: 'Monitoring & Adjustment', description: 'Track impact and adjust strategies', duration: 'Ongoing', connector: 'monitoring' },
        ];
        
        const groupedPersonnelData = groupPersonnelRecommendations(personnelRecs);
        
        return {
          title: `Budget Simulation: ${analysisData.metadata?.percentage || 10}% ${analysisData.metadata?.scenario || 'cut'} for ${analysisData.metadata?.department || 'engineering'}`,
          summary: `Intelligent financial impact analysis with people optimization insights`,
          departmentImpact,
          savingsBreakdown,
          implementationTimeline,
          workflowSteps: budgetWorkflowSteps,
          personnelRecommendations: personnelRecs,
          groupedPersonnelData,
          contractorCount,
          lowPerformerCount,
          highPotentialCount,
          headcountChange,
          totalPersonnelImpact,
          metrics: [
            { label: 'Total Impact', value: `$${Math.abs(analysisData.scenarioAnalysis?.totalImpact || 500000).toLocaleString()}`, color: 'text-purple-600' },
            { label: 'Personnel Savings', value: `$${Math.abs(totalPersonnelImpact).toLocaleString()}`, color: 'text-blue-600' },
            { label: 'Headcount Change', value: headcountChange, color: headcountChange < 0 ? 'text-red-600' : 'text-green-600' },
            { label: 'Alignment Score', value: `${analysisData.strategicImpact?.alignmentScore || 85}%`, color: 'text-green-600' },
          ],
          recommendations: [
            contractorCount > 0 ? `Optimize ${contractorCount} contractor agreements for immediate savings` : 'Review vendor contracts',
            lowPerformerCount > 0 ? `Implement performance plans for ${lowPerformerCount} low performers` : 'Optimize team processes',
            highPotentialCount > 0 ? `Invest in ${highPotentialCount} high potential individuals` : 'Focus on strategic investments',
            'Maintain training budget for skill development',
          ],
          charts: ['department', 'savings', 'timeline', 'personnel']
        };

      case 'ai-coding-impact':
        // AI Coding Impact specific data
        const productivityTrend = analysisData.visualizationData?.productivityTrend || [
          { week: 1, withAI: 65, withoutAI: 60 },
          { week: 2, withAI: 68, withoutAI: 61 },
          { week: 3, withAI: 72, withoutAI: 59 },
          { week: 4, withAI: 75, withoutAI: 62 },
          { week: 5, withAI: 78, withoutAI: 63 },
          { week: 6, withAI: 80, withoutAI: 64 },
          { week: 7, withAI: 82, withoutAI: 65 },
          { week: 8, withAI: 85, withoutAI: 66 },
        ];
        
        const defectTrend = analysisData.visualizationData?.defectTrend || [
          { week: 1, withAI: 12, withoutAI: 15 },
          { week: 2, withAI: 10, withoutAI: 14 },
          { week: 3, withAI: 8, withoutAI: 16 },
          { week: 4, withAI: 9, withoutAI: 13 },
          { week: 5, withAI: 7, withoutAI: 14 },
          { week: 6, withAI: 6, withoutAI: 15 },
          { week: 7, withAI: 5, withoutAI: 12 },
          { week: 8, withAI: 4, withoutAI: 11 },
        ];
        
        const tokenUsageCorrelation = analysisData.visualizationData?.tokenUsageCorrelation || [
          { tokens: 1000, qualityScore: 78 },
          { tokens: 1500, qualityScore: 82 },
          { tokens: 2000, qualityScore: 85 },
          { tokens: 2500, qualityScore: 81 },
          { tokens: 3000, qualityScore: 76 },
          { tokens: 3500, qualityScore: 70 },
        ];
        
        const skillLevelImpact = analysisData.teamImpact?.skillLevelImpact || [
          { skillLevel: 'junior', productivityGain: 35, qualityChange: 15 },
          { skillLevel: 'mid', productivityGain: 25, qualityChange: 10 },
          { skillLevel: 'senior', productivityGain: 15, qualityChange: 5 },
        ];
        
        const optimizationOpportunities = analysisData.tokenUsage?.optimizationOpportunities || [
          { pattern: "High token usage for boilerplate code", impact: 15, recommendation: "Create custom code snippets" },
          { pattern: "Over-reliance on AI for complex logic", impact: 25, recommendation: "Balance with code reviews" },
          { pattern: "Duplicate AI-generated code", impact: 10, recommendation: "Implement shared patterns library" },
        ];
        
        const aiCodingWorkflowSteps = [
          { step: 1, title: 'Token Usage Analysis', description: 'Analyze AI coding assistant token consumption patterns', duration: '2 days', connector: 'token' },
          { step: 2, title: 'Productivity Impact Assessment', description: 'Measure coding velocity and completion time improvements', duration: '3 days', connector: 'productivity' },
          { step: 3, title: 'Code Quality Analysis', description: 'Analyze defect rates, code review time, and complexity scores', duration: '2 days', connector: 'quality' },
          { step: 4, title: 'ROI Calculation', description: 'Calculate return on investment from AI coding tools', duration: '1 day', connector: 'roi' },
          { step: 5, title: 'Team Impact Assessment', description: 'Evaluate impact across different skill levels and adoption rates', duration: '2 days', connector: 'analysis' },
          { step: 6, title: 'Monitoring Setup', description: 'Set up ongoing monitoring dashboard for AI tool effectiveness', duration: '3 days', connector: 'monitoring' },
        ];
        
        return {
          title: 'Generate AI Coding Impact Analysis',
          summary: 'Comprehensive analysis of AI coding assistant productivity, quality, and ROI impact',
          productivityTrend,
          defectTrend,
          tokenUsageCorrelation,
          skillLevelImpact,
          optimizationOpportunities,
          workflowSteps: aiCodingWorkflowSteps,
          metrics: [
            { label: 'Overall Impact', value: `+${analysisData.summary?.overallImpact || 18}%`, color: 'text-green-600' },
            { label: 'ROI', value: `${analysisData.tokenUsage?.roi || 240}%`, color: 'text-blue-600' },
            { label: 'Adoption Rate', value: `${analysisData.teamImpact?.adoptionRate || 60}%`, color: 'text-purple-600' },
            { label: 'Confidence', value: `${analysisData.summary?.confidence || 87}%`, color: 'text-indigo-600' },
          ],
          recommendations: analysisData.recommendations?.map((rec: any) => rec.action) || [
            'Expand AI tool access to all development teams',
            'Run training sessions on effective AI tool usage',
            'Optimize token usage patterns identified in analysis',
            'Monitor AI-generated code quality monthly',
          ],
          charts: ['productivity', 'quality', 'tokens', 'skillImpact']
        };

      default:
        return {
          title: 'Detailed Analysis',
          summary: 'Comprehensive analysis report',
          metrics: [],
          workflowSteps: [],
          recommendations: [],
          charts: []
        };
    }
  };

  const data = generateCapabilitySpecificData();

  useEffect(() => {
    if (autoStartWorkflow && !hasAutoStarted) {
      setActiveTab('workflow');
      setHasAutoStarted(true);
      
      setTimeout(() => {
        startSimulation();
      }, 500);
    }
  }, [autoStartWorkflow, hasAutoStarted]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isSimulationRunning && currentStep < data.workflowSteps?.length) {
      interval = setInterval(() => {
        setCurrentStep(prev => {
          const nextStep = prev + 1;
          if (nextStep >= data.workflowSteps.length) {
            setIsSimulationRunning(false);
            return prev;
          }
          return nextStep;
        });
        
        setSimulationProgress(prev => {
          const progress = ((currentStep + 1) / data.workflowSteps.length) * 100;
          return Math.min(progress, 100);
        });
      }, 2000);
    }
    
    return () => clearInterval(interval);
  }, [isSimulationRunning, currentStep, data.workflowSteps]);

  const startSimulation = () => {
    setIsSimulationRunning(true);
    setCurrentStep(0);
    setSimulationProgress(0);
  };

  const pauseSimulation = () => {
    setIsSimulationRunning(false);
  };

  const resetSimulation = () => {
    setIsSimulationRunning(false);
    setCurrentStep(0);
    setSimulationProgress(0);
    setHasAutoStarted(false);
  };

  const handleTabChange = (tab: 'overview' | 'charts' | 'workflow' | 'insights') => {
    setActiveTab(tab);
    if (tab !== 'workflow') {
      resetSimulation();
    }
  };

  const renderCharts = () => {
    switch (capability) {
      case 'team-rebalancing':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Transition Timeline</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.timelineData || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="productivity" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Productivity" />
                      <Area type="monotone" dataKey="risk" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} name="Risk Level" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Skill Assessment</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={data.skillData || []}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="skill" />
                      <PolarRadiusAxis />
                      <Radar name="Skill Level" dataKey="level" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Team Impact Analysis</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.teamImpactData || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'bottleneck-diagnosis':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Root Cause Analysis</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.bottleneckData || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Recovery Timeline</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.recoveryTimeline || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="progress" stroke="#10b981" strokeWidth={2} />
                    <Line type="monotone" dataKey="risk" stroke="#ef4444" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'initiative-planner':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Budget Breakdown</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.initiativeData || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="phase" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
                    <Bar dataKey="cost" fill="#8b5cf6" name="Cost" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Team Composition</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.teamComposition || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ role, percent }) => `${role}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="cost"
                    >
                      {data.teamComposition?.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value}`, 'Cost']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'budget-simulator':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Department Impact</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.departmentImpact || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`$${value}`, 'Budget']} />
                      <Legend />
                      <Bar dataKey="current" fill="#94a3b8" name="Current Budget" />
                      <Bar dataKey="new" fill="#8b5cf6" name="New Budget" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Savings Breakdown</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.savingsBreakdown || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`$${value}`, 'Savings']} />
                      <Bar dataKey="savings" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            {data.groupedPersonnelData && data.groupedPersonnelData.length > 0 && (
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Personnel Optimization Insights</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={data.groupedPersonnelData}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" />
                      <YAxis 
                        type="category" 
                        dataKey="category"
                        width={180}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip formatter={(value: number, name: string) => {
                        if (name === 'impact') {
                          return [`$${value.toLocaleString()}`, 'Total Impact'];
                        }
                        if (name === 'avgImpact') {
                          return [`$${value.toLocaleString()}`, 'Average per Person'];
                        }
                        return [value, name];
                      }} />
                      <Bar 
                        dataKey="impact" 
                        fill="#ec4899"
                        name="Total Financial Impact"
                        radius={[0, 4, 4, 0]}
                      >
                        {data.groupedPersonnelData.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={
                            entry.category.includes('Contractor') ? '#8b5cf6' :
                            entry.category.includes('Low Performer') ? '#ef4444' :
                            entry.category.includes('High Performer') ? '#10b981' :
                            entry.category.includes('Investment') ? '#f59e0b' :
                            '#ec4899'
                          } />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="font-bold text-blue-700 text-lg">
                      {data.contractorCount || 0}
                    </div>
                    <div className="text-sm text-blue-600">Contractors</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="font-bold text-red-700 text-lg">
                      {data.lowPerformerCount || 0}
                    </div>
                    <div className="text-sm text-red-600">Low Performers</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="font-bold text-green-700 text-lg">
                      {data.highPotentialCount || 0}
                    </div>
                    <div className="text-sm text-green-600">High Potential</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="font-bold text-purple-700 text-lg">
                      {data.headcountChange || 0}
                    </div>
                    <div className="text-sm text-purple-600">Headcount Change</div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-xl border p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Implementation Timeline</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.implementationTimeline || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value}`, 'Cumulative Savings']} />
                    <Area type="monotone" dataKey="cumulativeSavings" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      case 'ai-coding-impact':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Productivity Trend (With vs Without AI)</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.productivityTrend || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" label={{ value: 'Week', position: 'insideBottom', offset: -5 }} />
                      <YAxis label={{ value: 'Productivity Score', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="withAI" stroke="#10b981" strokeWidth={2} name="With AI" />
                      <Line type="monotone" dataKey="withoutAI" stroke="#ef4444" strokeWidth={2} name="Without AI" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Defect Rate Trend (With vs Without AI)</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.defectTrend || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" label={{ value: 'Week', position: 'insideBottom', offset: -5 }} />
                      <YAxis label={{ value: 'Defects per 1K LOC', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="withAI" stroke="#3b82f6" strokeWidth={2} name="With AI" />
                      <Line type="monotone" dataKey="withoutAI" stroke="#f59e0b" strokeWidth={2} name="Without AI" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Token Usage vs Code Quality Correlation</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number" 
                        dataKey="tokens" 
                        name="Tokens per Day"
                        label={{ value: 'Daily Tokens', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="qualityScore" 
                        name="Code Quality Score"
                        label={{ value: 'Quality Score', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                      <Scatter name="Quality Correlation" data={data.tokenUsageCorrelation || []} fill="#8b5cf6" />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Impact by Skill Level</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.skillLevelImpact || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="skillLevel" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="productivityGain" fill="#10b981" name="Productivity Gain (%)" />
                      <Bar dataKey="qualityChange" fill="#3b82f6" name="Quality Change (%)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            {data.optimizationOpportunities && data.optimizationOpportunities.length > 0 && (
              <div className="bg-white rounded-xl border p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Token Optimization Opportunities</h4>
                <div className="space-y-4">
                  {data.optimizationOpportunities.map((opportunity: any, index: number) => (
                    <div key={index} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-medium text-gray-900">{opportunity.pattern}</h5>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          Impact: {opportunity.impact}%
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{opportunity.recommendation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <div className="bg-white rounded-xl border p-6">
            <p className="text-gray-600">No charts available for this analysis.</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-7xl h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="border-b border-gray-200 p-6 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-xl ${
              capability === 'team-rebalancing' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
              capability === 'bottleneck-diagnosis' ? 'bg-gradient-to-r from-orange-500 to-red-500' :
              capability === 'initiative-planner' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
              capability === 'ai-coding-impact' ? 'bg-gradient-to-r from-indigo-500 to-purple-500' :
              'bg-gradient-to-r from-purple-500 to-pink-500'
            }`}>
              {capability === 'team-rebalancing' && <Users className="text-white" size={24} />}
              {capability === 'bottleneck-diagnosis' && <AlertTriangle className="text-white" size={24} />}
              {capability === 'initiative-planner' && <Target className="text-white" size={24} />}
              {capability === 'budget-simulator' && <DollarSign className="text-white" size={24} />}
              {capability === 'ai-coding-impact' && <Code className="text-white" size={24} />}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{data.title}</h2>
              <p className="text-gray-600">{data.summary}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Download size={20} className="text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Share2 size={20} className="text-gray-600" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-600 hover:text-red-600" />
            </button>
          </div>
        </div>

        <div className="border-b">
          <div className="flex space-x-1 px-6 pt-4">
            {['overview', 'charts', 'workflow', 'insights'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab as any)}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-white border-t border-l border-r border-gray-200 text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={activeTab === tab ? { 
                  color: capability === 'team-rebalancing' ? '#3b82f6' :
                         capability === 'bottleneck-diagnosis' ? '#f59e0b' :
                         capability === 'initiative-planner' ? '#10b981' :
                         capability === 'ai-coding-impact' ? '#8b5cf6' : '#8b5cf6'
                } : {}}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {data.metrics?.map((metric: any, index: number) => (
                  <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                    <p className="text-3xl font-bold mb-2" style={{ 
                      color: metric.color.replace('text-', '').replace('600', '500') 
                    }}>
                      {metric.value}
                    </p>
                    <p className="text-sm text-gray-600">{metric.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100 p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                  <Zap className="mr-2" size={20} style={{ 
                    color: capability === 'team-rebalancing' ? '#3b82f6' :
                           capability === 'bottleneck-diagnosis' ? '#f59e0b' :
                           capability === 'initiative-planner' ? '#10b981' :
                           capability === 'ai-coding-impact' ? '#8b5cf6' : '#8b5cf6'
                  }} />
                  Key Recommendations
                </h3>
                <div className="space-y-3">
                  {data.recommendations?.map((rec: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ 
                          backgroundColor: capability === 'team-rebalancing' ? '#3b82f6' :
                                         capability === 'bottleneck-diagnosis' ? '#f59e0b' :
                                         capability === 'initiative-planner' ? '#10b981' :
                                         capability === 'ai-coding-impact' ? '#8b5cf6' : '#8b5cf6'
                        }}
                      ></div>
                      <p className="text-gray-800">{rec}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {capability === 'budget-simulator' && data.personnelRecommendations && data.personnelRecommendations.length > 0 && (
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-purple-100 p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="mr-2" size={20} />
                    Personnel Intelligence Insights
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.contractorCount > 0 && (
                      <div className="bg-white/80 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">Contractor Optimization</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                            {data.contractorCount} identified
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">Contractors provide flexibility for immediate budget adjustments</p>
                      </div>
                    )}
                    
                    {data.lowPerformerCount > 0 && (
                      <div className="bg-white/80 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">Performance Management</span>
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                            {data.lowPerformerCount} identified
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">Low performers flagged for performance improvement plans</p>
                      </div>
                    )}
                    
                    {data.highPotentialCount > 0 && (
                      <div className="bg-white/80 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">High Potential Investment</span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            {data.highPotentialCount} identified
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">Individuals ready for strategic development investments</p>
                      </div>
                    )}
                    
                    <div className="bg-white/80 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">Headcount Impact</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          data.headcountChange < 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {data.headcountChange > 0 ? '+' : ''}{data.headcountChange} change
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Total personnel impact on organizational structure</p>
                    </div>
                  </div>
                </div>
              )}
              
              {capability === 'ai-coding-impact' && data.optimizationOpportunities && data.optimizationOpportunities.length > 0 && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <Code className="mr-2" size={20} />
                    AI Coding Tool Optimization Opportunities
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {data.optimizationOpportunities.slice(0, 3).map((opportunity: any, index: number) => (
                      <div key={index} className="bg-white/80 p-4 rounded-lg border border-blue-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">Pattern {index + 1}</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {opportunity.impact}% impact
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{opportunity.pattern}</p>
                        <p className="text-xs text-gray-600">Recommendation: {opportunity.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'charts' && renderCharts()}

          {activeTab === 'workflow' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-6">Agentic Workflow</h3>
                
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Progress</span>
                    <span>{Math.round(simulationProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${simulationProgress}%`,
                        backgroundColor: capability === 'team-rebalancing' ? '#3b82f6' :
                                       capability === 'bottleneck-diagnosis' ? '#f59e0b' :
                                       capability === 'initiative-planner' ? '#10b981' :
                                       capability === 'ai-coding-impact' ? '#8b5cf6' : '#8b5cf6'
                      }}
                    ></div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {data.workflowSteps?.map((step: any, index: number) => {
                    const isCompleted = index < currentStep;
                    const isActive = index === currentStep && isSimulationRunning;
                    const connector = step.connector || getConnectorForStep(capability, index);
                    
                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border transition-all ${
                          isCompleted
                            ? 'bg-green-50 border-green-200'
                            : isActive
                            ? 'bg-blue-50 border-blue-200 animate-pulse'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isCompleted ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                              {isCompleted ? (
                                <CheckCircle className="text-white" size={16} />
                              ) : (
                                <span className="text-white font-bold">{step.step}</span>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">{step.title}</h4>
                                {connector && (
                                  <div className="flex items-center space-x-1 bg-gray-100 px-2 py-1 rounded-lg">
                                    {connectorIcons[connector] || <Settings size={14} className="text-gray-600" />}
                                    <span className="text-xs text-gray-700 capitalize">
                                      {connector.replace('-', ' ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{step.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {step.duration}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-6 flex space-x-3">
                  <button
                    onClick={isSimulationRunning ? pauseSimulation : startSimulation}
                    className="px-4 py-2 rounded-lg text-white flex items-center space-x-2 transition-colors"
                    style={{ 
                      backgroundColor: capability === 'team-rebalancing' ? '#3b82f6' :
                                     capability === 'bottleneck-diagnosis' ? '#f59e0b' :
                                     capability === 'initiative-planner' ? '#10b981' :
                                     capability === 'ai-coding-impact' ? '#8b5cf6' : '#8b5cf6'
                    }}
                  >
                    {isSimulationRunning ? (
                      <>
                        <Pause size={16} />
                        <span>Pause Simulation</span>
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        <span>Start Simulation</span>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={resetSimulation}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center space-x-2 transition-colors"
                  >
                    <RefreshCw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {capability === 'budget-simulator' && data.personnelRecommendations && data.personnelRecommendations.length > 0 && (
                <div className="mt-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-purple-100 p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="mr-2" size={20} />
                    Personnel Intelligence Insights
                  </h4>
                  <div className="space-y-3">
                    {data.personnelRecommendations.slice(0, 5).map((rec: any, index: number) => (
                      <div key={index} className={`p-3 rounded-lg ${
                        rec.type === 'reduce' ? 'bg-red-50 border border-red-200' :
                        rec.type === 'retain' ? 'bg-green-50 border border-green-200' :
                        rec.type === 'optimize' ? 'bg-yellow-50 border border-yellow-200' :
                        rec.type === 'invest' ? 'bg-blue-50 border border-blue-200' :
                        'bg-gray-50 border border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium ${
                            rec.type === 'reduce' ? 'text-red-700' :
                            rec.type === 'retain' ? 'text-green-700' :
                            rec.type === 'optimize' ? 'text-yellow-700' :
                            rec.type === 'invest' ? 'text-blue-700' :
                            'text-gray-700'
                          }`}>
                            {rec.type.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">{rec.timeline}</span>
                        </div>
                        <p className="text-sm text-gray-800">{rec.justification}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-600">Financial Impact:</span>
                          <span className={`text-xs font-medium ${
                            rec.impact > 0 ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            ${Math.abs(rec.impact).toLocaleString()} {rec.impact > 0 ? 'savings' : 'investment'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button className="p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <MessageSquare size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Notify Team</p>
                        <p className="text-sm text-gray-500">Send automated notifications</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-gray-400" />
                  </button>
                  
                  <button className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Calendar size={20} className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Schedule Meetings</p>
                        <p className="text-sm text-gray-500">Book calendar invites</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100 p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                  <Brain className="mr-2" size={20} style={{ 
                    color: capability === 'team-rebalancing' ? '#3b82f6' :
                           capability === 'bottleneck-diagnosis' ? '#f59e0b' :
                           capability === 'initiative-planner' ? '#10b981' :
                           capability === 'ai-coding-impact' ? '#8b5cf6' : '#8b5cf6'
                  }} />
                  AI Insights & Patterns
                </h3>
                <div className="space-y-4">
                  <div className="bg-white/80 p-4 rounded-lg">
                    <p className="text-gray-900">
                      Based on analysis of 156 similar cases, this scenario has a{' '}
                      <span className="font-bold text-green-600">78% higher success rate</span>{' '}
                      when implemented with phased transitions.
                    </p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg">
                    <p className="text-gray-900">
                      Historical data shows that teams with similar skill gaps experience{' '}
                      <span className="font-bold text-orange-600">42% longer adjustment periods</span>{' '}
                      without structured onboarding.
                    </p>
                  </div>
                  <div className="bg-white/80 p-4 rounded-lg">
                    <p className="text-gray-900">
                      Companies that implement automated workflow monitoring see{' '}
                      <span className="font-bold text-blue-600">65% faster issue resolution</span>{' '}
                      compared to manual tracking.
                    </p>
                  </div>
                  
                  {capability === 'budget-simulator' && (
                    <>
                      <div className="bg-white/80 p-4 rounded-lg">
                        <p className="text-gray-900">
                          Intelligent budget optimization focusing on contractor adjustments and performance management yields{' '}
                          <span className="font-bold text-purple-600">35% better retention</span>{' '}
                          compared to across-the-board cuts.
                        </p>
                      </div>
                      <div className="bg-white/80 p-4 rounded-lg">
                        <p className="text-gray-900">
                          Analysis shows that organizations using personnel intelligence see{' '}
                          <span className="font-bold text-green-600">48% higher ROI</span>{' '}
                          on budget optimization initiatives.
                        </p>
                      </div>
                    </>
                  )}
                  
                  {capability === 'ai-coding-impact' && (
                    <>
                      <div className="bg-white/80 p-4 rounded-lg">
                        <p className="text-gray-900">
                          AI coding tools show maximum benefit for junior developers with{' '}
                          <span className="font-bold text-green-600">35% productivity gains</span>{' '}
                          compared to 15% for senior developers.
                        </p>
                      </div>
                      <div className="bg-white/80 p-4 rounded-lg">
                        <p className="text-gray-900">
                          Optimal token usage (1,500-2,000 per day) correlates with{' '}
                          <span className="font-bold text-blue-600">85% code quality scores</span>{' '}
                          - beyond which quality declines.
                        </p>
                      </div>
                      <div className="bg-white/80 p-4 rounded-lg">
                        <p className="text-gray-900">
                          Teams using AI coding tools see{' '}
                          <span className="font-bold text-purple-600">28% faster task completion</span>{' '}
                          with 33% reduction in defects.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-4">
              <span>Generated by AI Chief of Staff • Analysis ID: {analysisData.metadata?.timestamp || Date.now().toString(36)}</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                capability === 'team-rebalancing' ? 'bg-blue-100 text-blue-800' :
                capability === 'bottleneck-diagnosis' ? 'bg-orange-100 text-orange-800' :
                capability === 'initiative-planner' ? 'bg-green-100 text-green-800' :
                capability === 'ai-coding-impact' ? 'bg-indigo-100 text-indigo-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {capability.replace('-', ' ').toUpperCase()}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={14} />
              <span>Last updated: {new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedAnalysis;